
import { useCallback, useContext, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "react-toastify";

const usePosOrders = () => {
  const { api, userID } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [billLoader, setBillLoader] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [catalogueStore, setCatalogueStore] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [taxConfig, setTaxConfig] = useState(null);
  const [taxConfigLoading, setTaxConfigLoading] = useState(false);
  const [orderOptions, setOrderOptions] = useState([]);
  const [orderOptionsLoading, setOrderOptionsLoading] = useState(false);
  const [posButtonsMaster, setPosButtonsMaster] = useState([]);
  const [posButtonsMasterLoading, setPosButtonsMasterLoading] = useState(false);
  const [insertPosOptionsLoading, setInsertPosOptionsLoading] = useState(false);

  const normalizeOrderType = (type) => {
    const value = String(type || "")
      .trim()
      .toLowerCase();

    if (
      value === "takeway" ||
      value === "take away" ||
      value === "take-away" ||
      value === "takeaway"
    ) {
      return "takeaway";
    }

    if (
      value === "homedelivery" ||
      value === "home delivery" ||
      value === "home-delivery" ||
      value === "home_delivery"
    ) {
      return "homedelivery";
    }

    if (value === "dinein" || value === "dine-in" || value === "dining") {
      return "dining";
    }

    if (
      value === "room_order" ||
      value === "room order" ||
      value === "roomorder"
    ) {
      return "room_order";
    }

    return value || "takeaway";
  };

  //  GET ORDERS
  const getOrders = async ({ fromDate, toDate }) => {
    setLoading(true);

    try {
      const res = await api.post("/Order/v1.0/getPosOrders", {
        userid: userID,
        secret: "string",
        fromdate: fromDate,
        todate: toDate,
        status: ["hold", "completed", "open", "closed"],
      });

      const responseCode = res.data?.responseCode;
      // const responseMessage = res.data?.responseMessage;

      if (responseCode === "00") {
        setOrders(res.data?.response || []);
        return res.data;
      }

      // responseCode 01 / other failed response
      setOrders([]);
      // toast.error(responseMessage || "Failed to fetch orders");
      return res.data;
    } catch (err) {
      console.error(err);

      setOrders([]);

      const errorMessage =
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        err?.message ||
        "Error fetching orders";

      // toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  //  GET ORDERS
  const getOrdersV2 = async ({ fromDate, toDate }) => {
    setLoading(true);
    try {
      const res = await api.post("/Order/v1.0/getPosOrdersV1", {
        userid: userID,
        secret: "string",
        fromdate: fromDate,
        todate: toDate,
        status: ["hold", "completed", "open", "closed"],
      });

      if (res.data?.responseCode === "00") {
        setOrders(res.data.response || []);
      } else {
        setOrders([]);
        // toast("No Order Found");
      }
    } catch (err) {
      console.error(err);
      // toast.error("Error fetching orders");
    } finally {
      setLoading(false);
    }
  };

  //  SUMMARY
  const getSummary = async ({ fromDate, toDate }) => {
    setSummaryLoading(true);

    try {
      const res = await api.post("/Order/v1.0/getPosOrdersSummaryData", {
        userid: userID,
        secret: "string",
        fromdate: fromDate,
        todate: toDate,
      });

      if (res.data?.responseCode === "00") {
        setSummaryData(res.data);
        return res.data;
      } else {
        setSummaryData(null);
      }
    } catch (err) {
      console.error(err);
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  //  ORDER DETAILS
  const getOrderDetails = async (orderid) => {
    setBillLoader(true);
    try {
      const res = await api.post("/Order/v1.0/getPosOrderDetails", { orderid });

      if (res.data?.responseCode === "00") {
        setOrderDetails(res.data.response);
        return res.data.response;
      } else {
        // toast("No Order Found");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setBillLoader(false);
    }
  };

  const generateOrderID = async ({ mobile, orderType }) => {
    if (!mobile) {
      toast.error("Mobile number missing");
      return null;
    }

    const mobileNumber = typeof mobile === "object" ? mobile.mobile : mobile;
    const normalizedOrderType = normalizeOrderType(orderType);

    const payload = {
      userid: userID,
      custMobile: mobileNumber,
      orderType: normalizedOrderType,
    };

    try {
      const res = await api.post("/Order/v1.0/generateOrderNumber", payload);

      const responseCode = res.data?.responseCode;
      const responseMessage = res.data?.responseMessage;

      if (responseCode === "00") {
        const id = res.data?.data?.orderId || "";
        setOrderId(id);
        return id;
      }

      toast.error(responseMessage || "Order id not generated");
      return null;
    } catch (err) {
      console.error("Generate Order ID Error:", err);

      const errorMessage =
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        err?.message ||
        "Error generating order id";

      toast.error(errorMessage);
      return null;
    }
  };

  const getCatalog = async ({ orderType, force = false } = {}) => {
    const normalizedType = normalizeOrderType(orderType);

    const catalogOrderType =
      normalizedType === "room_order" ? "dining" : normalizedType;

    const storageKey = `dine_catalog_cache_${userID}_${catalogOrderType || "all"}`;

    try {
      setCatalogLoading(true);

      if (!force) {
        const cached = sessionStorage.getItem(storageKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          setCatalogueStore(parsed);
          return parsed;
        }
      }

      const response = await api.post("/Order/v2.0/getCatalogItems", {
        userid: userID,
        orderType: catalogOrderType,
        platform: "pos",
      });

      if (response.data?.responseCode === "00") {
        const data = response.data.response.menu || [];
        setCatalogueStore(data);
        sessionStorage.setItem(storageKey, JSON.stringify(data));
        return data;
      }

      setCatalogueStore([]);
      return [];
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      setCatalogLoading(false);
    }
  };

  const placePosOrder = async (payload) => {
    try {
      setLoading(true);

      const res = await api.post("/Order/v2.0/placePosOrder", payload);

      if (res.data?.responseCode === "00") {
        // toast.success("Order placed successfully");
        return res.data;
      } else {
        toast.error(res.data?.responseMessage || "Order failed");
        return null;
      }
    } catch (err) {
      console.error(err);
      toast.error("Error placing order");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getDiscountList = async ({ amount, orderId }) => {
    try {
      const res = await api.post("/Order/v1.0/getPosDiscountList", {
        amount,
        orderType: orderId,
        userid: userID,
      });

      if (res.data?.responseCode === "00") {
        return res.data.response || [];
      } else {
        toast("No discount found");
        return [];
      }
    } catch (err) {
      console.error(err);
      toast.error("Error fetching discounts");
      return [];
    }
  };

  const selectDiscountPosOrder = async ({ orderId, discountId }) => {
    if (!orderId) {
      toast.error("Order ID missing");
      return null;
    }

    if (!discountId) {
      toast.error("Discount ID missing");
      return null;
    }

    try {
      const res = await api.post("/Order/v1.0/selectDiscountPosOrder", {
        orderId: orderId,
        discountId: discountId,
      });

      if (res.data?.responseCode === "00") {
        // toast.success(res.data?.responseMessage || "Discount applied");
        return res.data;
      }

      toast.error(res.data?.responseMessage || "Failed to apply discount");
      return null;
    } catch (err) {
      console.error("Select Discount POS Order Error:", err);

      const errorMessage =
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        err?.message ||
        "Error applying discount";

      toast.error(errorMessage);
      return null;
    }
  };

  const removeDiscountPosOrder = async ({ orderId, discountId }) => {
    if (!orderId) {
      toast.error("Order ID missing");
      return null;
    }

    if (!discountId) {
      toast.error("Discount ID missing");
      return null;
    }

    try {
      const res = await api.post("/Order/v1.0/removeDiscountPosOrder", {
        orderId: orderId,
        discountId: discountId,
      });

      if (res.data?.responseCode === "00") {
        // toast.success(res.data?.responseMessage || "Discount removed");
        return res.data;
      }

      toast.error(res.data?.responseMessage || "Failed to remove discount");
      return null;
    } catch (err) {
      console.error("Remove Discount POS Order Error:", err);

      const errorMessage =
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        err?.message ||
        "Error removing discount";

      toast.error(errorMessage);
      return null;
    }
  };

  const generatePaymentIntent = async ({
    customerMobile,
    custName,
    orderId,
  }) => {
    try {
      const mobileNumber =
        typeof customerMobile === "object"
          ? customerMobile.mobile
          : customerMobile;

      const res = await api.post("/Order/v2.0/generatePaymentIntent", {
        userid: userID,
        custMobile: mobileNumber,
        custName: custName || "Customer",
        orderId,
      });

      if (res.data.responseCode === "00") {
        return res.data.response;
      }
    } catch (err) {
      console.error("Generate Payment Intent Error:", err);
    }
  };

  const getPaymentStatus = async (orderId) => {
    try {
      const res = await api.post("/Order/v1.0/getPosOrderPaymentDetails", {
        orderId,
      });

      if (res.data.responseCode === "00") {
        return res.data.response;
      }
    } catch (err) {
      console.error("Payment Status Error:", err);
    }
  };

  const getCustomerList = useCallback(
    async (mobileNumber) => {
      try {
        const digits = String(mobileNumber || "").replace(/\D/g, "");

        const tenDigitMobile = digits.slice(-10);

        if (tenDigitMobile.length !== 10) {
          return [];
        }

        const res = await api.post("/Order/v1.0/getCustomerList", {
          digit: tenDigitMobile,
          userid: userID,
        });

        if (
          res.data?.responseCode === "00" &&
          Array.isArray(res.data?.response)
        ) {
          return res.data.response;
        }

        return [];
      } catch (err) {
        console.error("Get Customer List Error:", err);
        return [];
      }
    },
    [api, userID],
  );

  const addCustomerAddress = async (payload) => {
    try {
      const res = await api.post("/Order/v1.0/addDeliveryAddress", payload);

      if (res.data?.responseCode === "00") {
        return res.data;
      }
      return null;
    } catch (err) {
      console.error("Add Address Error:", err);
      return null;
    }
  };

  const setDefaultDeliveryAddress = async ({ address, custMobile }) => {
    try {
      const res = await api.post("/Order/v1.0/setDefaultDeliveryAddress", {
        address,
        custMobile,
        userid: userID,
      });

      if (res.data?.responseCode === "00") {
        return res.data;
      } else {
        return null;
      }
    } catch (err) {
      console.error("Set Default Address Error:", err);
      return null;
    }
  };

  const getOrganizationTaxConfig = async () => {
    try {
      setTaxConfigLoading(true);

      const response = await api.post(
        `/Admin/getOrganizationInfoV1?userid=${userID}`,
        {},
      );

      if (
        response?.data?.responseCode === "00" &&
        Array.isArray(response.data.response)
      ) {
        const freshData = response.data.response[0] || {};
        setTaxConfig(freshData);
        return freshData;
      }

      setTaxConfig(null);
      return {};
    } catch (error) {
      console.error("Tax config error:", error);
      setTaxConfig(null);
      return {};
    } finally {
      setTaxConfigLoading(false);
    }
  };

  const cancelPosOrder = async ({ orderId, reason }) => {
    try {
      const response = await api.post("/Order/v1.0/cancelPosOrder", {
        orderid: orderId,
        platform: "POS",
        reason: reason,
      });

      if (response?.data?.responseCode === "00") {
        return true;
      }

      return false;
    } catch (err) {
      console.error("Cancel order error:", err);
      return false;
    }
  };

  const updateCustomerDetails = async ({
    orderId,
    custName,
    custMobile,
    billingName = "",
    billingGst = "",
    address = "",
  }) => {
    try {
      const response = await api.post("/Order/v1.0/updateCustomerName", {
        orderId: orderId,
        custName: custName,
        custMobile: custMobile,
        billingName: billingName,
        billingGst: billingGst,
        address: address,
        userid: userID,
      });

      if (response?.data?.responseCode === "00") {
        return true;
      }

      return false;
    } catch (err) {
      console.error("Update customer error:", err);
      return false;
    }
  };

  const processPrintOrder = async (orderId) => {
    try {
      const res = await api.post("/Order/v1.0/processPrint", {
        orderId: orderId,
        userid: userID,
      });

      if (res.data?.responseCode === "00") {
        return res.data;
      } else {
        toast.error(res.data?.responseMessage || "Print failed");
        return null;
      }
    } catch (err) {
      console.error("Process Print Error:", err);
      toast.error("Error processing print");
      return null;
    }
  };

  const printKot = async (orderId) => {
    if (!orderId) {
      toast.error("Order ID missing");
      return null;
    }

    try {
      const res = await api.post("/Order/v1.0/printKot", {
        orderId: String(orderId),
      });

      if (res.data?.responseCode === "00") {
        toast.success("KOT generated successfully");
        return res.data;
      }

      toast.error(res.data?.responseMessage || "KOT print failed");
      return null;
    } catch (err) {
      console.error("Print KOT Error:", err);
      toast.error("Error printing KOT");
      return null;
    }
  };

  const assignDeliveryPartner = async ({ orderId }) => {
    try {
      const res = await api.post("/Order/v1.0/assignDeliveryPartner", {
        deliveryPartner: "packky",
        orderId: orderId,
        platform: "pos",
        userid: userID,
      });

      if (res.data?.responseCode === "00") {
        toast.success("Delivery partner assigned");
        return res.data;
      } else {
        toast.error(res.data?.responseMessage || "Assignment failed");
        return null;
      }
    } catch (err) {
      console.error("Assign Delivery Partner Error:", err);
      toast.error("Error assigning delivery partner");
      return null;
    }
  };

  // validate POS Drawer PIN
  const validatePosPin = async (payload) => {
    try {
      const res = await api.post("/Order/validatePosPin", payload);

      return res.data;
    } catch (err) {
      console.error("Validate POS PIN Error:", err);
      toast.error("Error validating POS PIN");
      return null;
    }
  };

  // DRAWER OPEN / CLOSE OPERATION
  const drawerOperation = async (payload) => {
    try {
      const res = await api.post("/Order/drawerOperation", payload, {
        headers: {
          "Content-Type": "application/json-patch+json",
          accept: "*/*",
        },
      });

      return res.data;
    } catch (err) {
      console.error("Drawer Operation Error:", err);
      toast.error("Error while processing drawer operation");
      return null;
    }
  };

  // GET CURRENT DRAWER STATUS / BALANCE
  const getDrawerCurrentBalance = async (payload) => {
    try {
      const res = await api.post("/Order/getDrawerCurrentBalance", payload, {
        headers: {
          "Content-Type": "application/json-patch+json",
          accept: "*/*",
        },
      });

      return res.data;
    } catch (err) {
      console.error("Get Drawer Current Balance Error:", err);
      return null;
    }
  };

  const getPosOrderOptions = async () => {
    try {
      setOrderOptionsLoading(true);

      const res = await api.post("/Order/v1.0/getPosOrderOptions", {
        userid: userID,
      });

      if (res.data?.responseCode === "00") {
        const data = res.data?.response || [];
        setOrderOptions(data);
        return data;
      }

      setOrderOptions([]);
      toast.error(res.data?.responseMessage || "Failed to fetch order options");
      return [];
    } catch (err) {
      console.error("Get POS Order Options Error:", err);
      setOrderOptions([]);
      toast.error(
        err?.response?.data?.responseMessage || "Error fetching order options",
      );
      return [];
    } finally {
      setOrderOptionsLoading(false);
    }
  };

  // GET POS BUTTONS MASTER
  const getPosButtonsMaster = async () => {
    try {
      setPosButtonsMasterLoading(true);

      const res = await api.post("/Order/v1.0/getPosButtonsMaster", {});

      if (res.data?.responseCode === "00") {
        const data = Array.isArray(res.data?.response) ? res.data.response : [];

        setPosButtonsMaster(data);
        return data;
      }

      setPosButtonsMaster([]);
      toast.error(
        res.data?.responseMessage || "Failed to fetch POS buttons master",
      );

      return [];
    } catch (err) {
      console.error("Get POS Buttons Master Error:", err);

      setPosButtonsMaster([]);

      const errorMessage =
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        err?.message ||
        "Error fetching POS buttons master";

      toast.error(errorMessage);
      return [];
    } finally {
      setPosButtonsMasterLoading(false);
    }
  };

  // INSERT / UPDATE POS ORDER OPTIONS

  const insertPosOrderOptions = async (options = []) => {
    if (!Array.isArray(options)) {
      toast.error("Options must be an array");
      return null;
    }

    const normalizeOptionValue = (value = "") => {
      const normalizedValue = String(value).trim().toLowerCase();

      if (
        ["takwaway", "takeway", "take away", "take-away", "takeaway"].includes(
          normalizedValue,
        )
      ) {
        return "takeaway";
      }

      if (
        [
          "home_delivery",
          "home delivery",
          "home-delivery",
          "homedelivery",
        ].includes(normalizedValue)
      ) {
        return "homedelivery";
      }

      if (
        ["dining", "dine-in", "dine in", "dinein"].includes(normalizedValue)
      ) {
        return "dinein";
      }

      if (
        ["roomorder", "room order", "room-order", "room_order"].includes(
          normalizedValue,
        )
      ) {
        return "room_order";
      }

      return normalizedValue;
    };

    const normalizedOptions = options
      .filter((item) => item?.label && item?.value)
      .map((item) => ({
        label: String(item.label).trim().toUpperCase(),
        value: normalizeOptionValue(item.value),
        status:
          String(item.status || "N")
            .trim()
            .toUpperCase() === "Y"
            ? "Y"
            : "N",
      }));

    if (normalizedOptions.length === 0) {
      toast.error("POS options data is missing");
      return null;
    }

    const uniqueOptions = Array.from(
      new Map(normalizedOptions.map((item) => [item.value, item])).values(),
    );

    const payload = {
      us_code: String(userID || "").trim(),
      options: uniqueOptions,
    };

    if (!payload.us_code) {
      toast.error("User code is missing");
      return null;
    }

    console.log("INSERT POS ORDER OPTIONS PAYLOAD:", payload);

    try {
      setInsertPosOptionsLoading(true);

      const res = await api.post("/Order/insertPosOrderOptions", payload, {
        headers: {
          "Content-Type": "application/json-patch+json",
          accept: "*/*",
        },
      });

      if (res.data?.responseCode === "00") {
        setOrderOptions(uniqueOptions);

        return res.data;
      }

      toast.error(
        res.data?.responseMessage || "Failed to save POS order options",
      );

      return null;
    } catch (err) {
      console.error("Insert POS Order Options Error:", err);

      const errorMessage =
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        err?.message ||
        "Error saving POS order options";

      toast.error(errorMessage);

      return null;
    } finally {
      setInsertPosOptionsLoading(false);
    }
  };

  const updatePosOrder = async ({ orderId }) => {
    if (!orderId) {
      toast.error("Order ID missing");
      return null;
    }

    try {
      setLoading(true);

      const payload = {
        orderid: String(orderId),
      };

      console.log("UPDATE POS ORDER PAYLOAD:", payload);

      const res = await api.post("/Order/updatePosOrder", payload);

      if (res.data?.responseCode === "00") {
        return res.data;
      }

      toast.error(res.data?.responseMessage || "Failed to update POS order");

      return null;
    } catch (err) {
      console.error("Update POS Order Error:", err);

      const errorMessage =
        err?.response?.data?.responseMessage ||
        err?.response?.data?.message ||
        err?.message ||
        "Error updating POS order";

      toast.error(errorMessage);

      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    billLoader,
    summaryLoading,
    orders,
    summaryData,
    orderDetails,
    orderId,
    catalog,
    catalogLoading,
    catalogueStore,
    taxConfig,
    taxConfigLoading,

    orderOptions,
    orderOptionsLoading,

    posButtonsMaster,
    posButtonsMasterLoading,
    insertPosOptionsLoading,

    getPosOrderOptions,
    getPosButtonsMaster,
    insertPosOrderOptions,

    getOrders,
    getOrdersV2,
    getSummary,
    getOrderDetails,
    generateOrderID,
    getCatalog,
    placePosOrder,
    getDiscountList,
    selectDiscountPosOrder,
    removeDiscountPosOrder,
    generatePaymentIntent,
    getPaymentStatus,
    getCustomerList,
    addCustomerAddress,
    setDefaultDeliveryAddress,
    getOrganizationTaxConfig,
    cancelPosOrder,
    updateCustomerDetails,
    processPrintOrder,
    printKot,
    assignDeliveryPartner,
    setCatalogueStore,
    validatePosPin,
    drawerOperation,
    getDrawerCurrentBalance,
    updatePosOrder,
  };
};

export default usePosOrders;

